import { VITE_API_URL, VITE_APP_BASENAME } from '@/config';
import { getCookie } from '@/utils';
import { useCallback } from 'react';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface UseChatStreamOptions {
  onError?: (error: Error) => void;
}

export const useChatStream = (
  projectId: string,
  options?: UseChatStreamOptions,
) => {
  const sendMessage = useCallback(
    async function* (messages: ChatMessage[]) {
      try {
        const authToken = getCookie('access_token');
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        if (authToken) {
          headers.Authorization = `Bearer ${authToken}`;
        }

        const response = await fetch(
          `${VITE_API_URL}${VITE_APP_BASENAME}/api/projects/${projectId}/chat-stream/`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ messages }),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('Response body is not readable');
        }

        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.trim()) {
              try {
                const data = JSON.parse(line);
                // バックエンドのJSONL形式は {"v": "text"}
                if (data.v) {
                  yield data.v;
                }
              } catch (_e) {
                // JSON パースに失敗した場合は、そのまま出力
                if (line.trim().length > 0) {
                  yield line;
                }
              }
            }
          }
        }
      } catch (error) {
        if (options?.onError) {
          options.onError(error as Error);
        }
        throw error;
      }
    },
    [projectId, options],
  );

  return { sendMessage };
};
